import { RouterContext } from '@brainfocus/web/router/router_context';
import { useNavigationState } from '@brainfocus/web/router/navigation_state';
import { useLoaderManager } from '@brainfocus/web/router/loader_context';
import { resolvePathname } from '@brainfocus/web/router/url_utils';
import { findMatchingRoute, routePathname } from '@brainfocus/web/router/route_utils';
import { useContext } from 'preact/hooks';

// Global reference to routes - will be set by Router component
let globalRoutes: any[] = [];
let topLevelRoutes: any[] = [];

export function setGlobalRoutes(routes: any[], pathPrefix = '/') {
    globalRoutes = routes;
    // Store top-level routes separately (when pathPrefix is root)
    if (pathPrefix === '/') {
        topLevelRoutes = routes;
    }
}

/**
 * Get navigate function with support to navigate to relative paths. The path
 * is relative to the parent router's route, rather than current pathname.
 */
export function useNavigate(): NavigateFunction {
    const routerContext = useContext(RouterContext);
    const navigationState = useNavigationState();
    const loaderManager = useLoaderManager();

    return async function navigate(path: string | MouseEvent, tabOption = false) {
        if (path instanceof MouseEvent) {
            path.preventDefault();
            path = (path.target as HTMLAnchorElement).getAttribute('href')!;
        }
        const resolvedPath = resolvePathname(routerContext.pathPrefix, path);
        const newTab = tabOption === true
            || (tabOption instanceof MouseEvent && (tabOption.button === 2 || tabOption.ctrlKey));
        if (newTab) {
            open(resolvedPath);
        } else {

            // For absolute paths, try top-level routes first, then fall back to current context routes
            let match = null;
            if (path.startsWith('/') && topLevelRoutes.length > 0) {
                // Search in top-level routes with root context
                match = findMatchingRoute(topLevelRoutes, { pathPrefix: '/' }, resolvedPath);
            }
            // If not found in top-level, search in current context routes
            if (!match) {
                match = findMatchingRoute(globalRoutes, routerContext, resolvedPath);
            }

            // Check if navigating to the same pathname (force reload scenario)
            // (compared without the query string, e.g. the preview's `?t=`, #374)
            const isReload = routePathname(resolvedPath) === window.location.pathname;

            // If route has a loader, execute it BEFORE navigating
            if (match && match.route.loader) {
                try {
                    // Start pending navigation to show loading indicator
                    navigationState.startNavigation(resolvedPath);

                    // If navigating to same path, invalidate cache to force fresh data
                    if (isReload) {
                        loaderManager.invalidate(match.route.path);
                    }

                    // Execute loader (blocks until data is loaded)
                    await loaderManager.loadRoute(match.route.path, match.route.loader, match.params);

                    // ONLY NOW navigate
                    history.pushState({}, '', resolvedPath);
                    dispatchEvent(new PopStateEvent('popstate', { state: {} }));

                    // Complete navigation
                    navigationState.completeNavigation();
                } catch (error) {
                    console.error('Loader failed:', error);
                    navigationState.cancelNavigation();
                    // Still navigate even on error to show error page
                    history.pushState({}, '', resolvedPath);
                    dispatchEvent(new PopStateEvent('popstate', { state: {} }));
                }
            } else {
                // No loader: navigate immediately (current behavior)
                navigationState.startNavigation(resolvedPath);

                history.pushState({}, '', resolvedPath);
                dispatchEvent(new PopStateEvent('popstate', { state: {} }));

                // Complete navigation immediately since no loader
                navigationState.completeNavigation();
            }
        }
    };
}

/**
 * Function which routes navigation through Router.
 *
 * @param pathname Absolute or relative (to parent router) URL path
 * @param newTab Either boolean deciding whether the link should be open in new
 *  tab, or MouseEvent which is used to decide whether to open in new tab
 *  (middle click / ctrl).
 */
export type NavigateFunction = (pathname: string | MouseEvent, newTab?: boolean | MouseEvent) => Promise<void>;
